import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import ShopIcon from '@mui/icons-material/Shop';

import { getValueWithTwoDecimalPlace } from 'helpers/formattedDollar';
import { ITEM_DESCRIPTION_AND_PRICES } from 'styles/constants/appearanceSectionNames';
import {
  getSectionNavMediumColor,
  getSectionNavLightColor
} from 'styles/helpers/getThemeStyles';

const Details = styled(Typography)(({ theme }) => ({
  color: getSectionNavMediumColor(theme),
  display: 'inline-flex',
  alignItems: 'center',
  marginLeft: 5
}));

const iconStyles = ({ theme }) => ({
  fill: getSectionNavLightColor(theme),
  width: '16px'
});

const StyledLocationOnIcon = styled(LocationOnIcon)(iconStyles);

const StyledShopIcon = styled(ShopIcon)(iconStyles);

const StyledScheduleIcon = styled(ScheduleIcon)(iconStyles);

const StyledDriveEtaIcon = styled(DriveEtaIcon)(iconStyles);

const DetailsBox = styled(Box)({
  display: 'flex',
  paddingTop: 7,
  paddingRight: 15
});

function MenuHeroLocationDetails({
  locationAddress,
  cart,
  displayLocationInformationOnLayout = true
}) {
  const {
    catering_delivery_fee: deliveryFee,
    shipping_street_address_1: fulfillmentAddress,
    min_order_amount: minOrderAmount,
    max_order_amount: maxOrderAmount,
    menu_lead_time: menuLeadTime
  } = cart;

  const hasLeadTime =
    menuLeadTime.lead_time_hours || menuLeadTime.lead_time_days;

  const leadTimeText = menuLeadTime.lead_time_hours
    ? `${menuLeadTime.lead_time_hours} hour notice`
    : `${menuLeadTime.lead_time_days} day notice`;

  const showDeliveryFee = fulfillmentAddress && deliveryFee > 0;

  const styles = { variant: ITEM_DESCRIPTION_AND_PRICES };
  return (
    <Box
      data-testid="menu-hero-location-details"
      sx={theme => ({
        display: 'flex',
        ml: '-5px',
        [theme.breakpoints.down('md')]: {
          flexWrap: 'wrap',
          justifyContent: 'center'
        }
      })}
    >
      {displayLocationInformationOnLayout && (
        <DetailsBox>
          <StyledLocationOnIcon />
          <Details component="p" {...styles}>
            {locationAddress}
          </Details>
        </DetailsBox>
      )}
      {minOrderAmount && (
        <DetailsBox>
          <StyledShopIcon />
          <Details component="p" {...styles}>
            {`Order Min. $${getValueWithTwoDecimalPlace(minOrderAmount)}`}
          </Details>
        </DetailsBox>
      )}
      {maxOrderAmount && (
        <DetailsBox>
          <StyledShopIcon />
          <Details component="p" {...styles}>
            {`Order Max. $${getValueWithTwoDecimalPlace(maxOrderAmount)}`}
          </Details>
        </DetailsBox>
      )}
      {hasLeadTime && (
        <DetailsBox>
          <StyledScheduleIcon />
          <Details component="p" {...styles}>
            {leadTimeText}
          </Details>
        </DetailsBox>
      )}
      {showDeliveryFee && (
        <DetailsBox>
          <StyledDriveEtaIcon />
          <Details component="p" {...styles}>
            {`Delivery Fee $${getValueWithTwoDecimalPlace(deliveryFee)}`}
          </Details>
        </DetailsBox>
      )}
    </Box>
  );
}

MenuHeroLocationDetails.propTypes = {
  cart: PropTypes.shape({
    catering_delivery_fee: PropTypes.string,
    shipping_street_address_1: PropTypes.string,
    min_order_amount: PropTypes.string,
    max_order_amount: PropTypes.string,
    menu_lead_time: PropTypes.shape({
      lead_time_days: PropTypes.number,
      lead_time_hours: PropTypes.number,
      cutoff_time: PropTypes.string
    }).isRequired
  }),
  locationAddress: PropTypes.string,
  displayLocationInformationOnLayout: PropTypes.bool
};

export default MenuHeroLocationDetails;
